import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useSessionPicker } from '../../../hooks/booking/useSessionPicker'
import { useBookingPricing } from '../../../hooks/booking/useBookingPricing'

import PageHeader from '../../../components/ui/pageHeader/PageHeader'
import BookingLegend from '../../../components/authenticated/booking/bookingLegend/BookingLegend'
import SummaryPanel from '../../../components/authenticated/booking/summaryPanel/SummaryPanel'
import SessionPicker, { SessionPickerDays, SessionPickerSetup } from '../../../components/authenticated/booking/sessionPicker/SessionPicker'
import BookingPageLayout from '../../../components/bookingPageLayout/BookingPageLayout'

import { useApp } from '../../../contexts/AppContext'
import { BannerKind } from '../../../types/contexts/AppContext'
import { BookingLocationState } from '../../../types/booking/booking'
import { formatMoney } from '../../../utils/booking/booking'

const parseId = (value: string | null) => {
  if (!value) {
    return undefined
  }
  const id = Number(value)
  return Number.isInteger(id) && id > 0 ? id : undefined
}

const GuestBooking = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { showBanner } = useApp()
  const { t } = useTranslation('bookings')
  const [draft] = useState(() => (location.state as BookingLocationState | null)?.bookingDraft)
  const clubId = parseId(searchParams.get('clubId')) ?? draft?.clubId

  const picker = useSessionPicker({ initial: { ...draft, clubId } })
  // Guests have no passes: prices come from availability (the server prices the checkout again).
  const pricing = useBookingPricing({ sessions: picker.selected, clubId: picker.clubId, passOwner: null })

  useEffect(() => {
    if (draft) {
      navigate(location.pathname + location.search, { replace: true, state: null })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (picker.error) {
      showBanner({ kind: BannerKind.ERR, msg: picker.error })
      picker.resetError()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picker.error])

  const handlePay = () => {
    if (picker.selected.length === 0) {
      return
    }
    navigate('/guest-booking/checkout', {
      state: { bookingDraft: picker.draft } satisfies BookingLocationState,
    })
  }

  return (
    <div className="guest-booking">
      <BookingPageLayout
        header={
          <PageHeader
            title={{
              eyebrow: t('booking.guest-booking.page-header.eyebrow'),
              h1: t('booking.guest-booking.page-header.title'),
            }}
            sub={t('booking.guest-booking.page-header.subtitle')}
          />
        }
        toolbar={<SessionPickerSetup picker={picker} />}
        days={<SessionPickerDays picker={picker} />}
        courts={<SessionPicker picker={picker} showDays={false} />}
        legend={<BookingLegend />}
        summary={
          <SummaryPanel
            lines={pricing.lines}
            total={pricing.total}
            showPrepaid={false}
            confirmLabel={t('booking.booking-courts.summary-panel.pay', {
              total: formatMoney(pricing.total),
            })}
            onConfirm={handlePay}
            onClear={picker.clear}
          />
        }
      />
    </div>
  )
}

export default GuestBooking
